// @flow

//
// FP
//
import * as $ from 'sanctuary-def';
import S from '../../trix-fp-fantasy';

//
// React
//
import React, { useEffect, useLayoutEffect, useMemo, type Node, type ComponentType } from 'react';

//
// Redux Fantasy
//
import {
    useLocalSelector,
    useDispatchLocal,
} from '../../redux-fantasy-reducers';

//
// Extenders
//
import withHalFormsAction from '../extender/withHalFormsAction';
import withHalFormsDataSource from '../extender/withHalFormsDataSource';
import {
    withComponentsRegistry,
} from '../../trix-web-components-registry';

//
// Material UI
//
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

//
// Components
//
import HalFormsComponent from './HalFormsComponent';

import { type HalFormsContainerRowProps } from './halformscontainer/HalFormsContainerRowProps';

import {
	getActionFactory,
	createAction,
} from '../tools/halFormsRedux';

//
// Props
//
type Props = {
    //
    // Components
    //
    HalFormsContainerRow: ComponentType<HalFormsContainerRowProps>,

    //
    // Hal forms template
    //
    __halFormsTemplate: any,

    //
    // Ducks
    //
    __ducks: {
        actions: any,
        selectors: any,
    },

    //
    // Refs
    //
    __dataSourcesRef: any,

    //
    // Components registry
    //
    __componentsRegistry: any,

    //
    // Data Source Extender props (in case data source set)
    //
    __dataSourceExtender: {
        dataSource: any,
        loadData: (params: any) => void,
        isDataLoadNeeded: (params: any) => boolean,
        isLoadingData: boolean,
        params: any,
        data: any,
    },

    //
    // Hal forms events dispatcher
    //
    setClientEventListener: (listener: any) => void,

    //
    // Container
    //
    container: any,

    //
    // Data
    //
    data: any,

    //
    // Handlers
    //
    onChange: (value: any, meta: any) => void,
    onClose: () => void,
} // End of Props


//
// Component
//
const HalFormsContainer = (props: Props): Node => {
    //
    // Hooks
    //
    const dispatchLocal = useDispatchLocal();
    
    // initially load data
    const dataSource = (props.__dataSourceExtender) ? props.__dataSourceExtender.dataSource : null;
    useLayoutEffect(() => {
        // skip initial data load in case of no data source specified for the container
        if (!dataSource) {
            return;
        }

        if (props.__dataSourceExtender && 
                !props.__dataSourceExtender.isDataLoadNeeded(props.data)) {
            return;
        }
        props.__dataSourceExtender.loadData(props.data);
    }, [dataSource, props.data]);

    // Add hal forms event dispatcher listener
    useEffect(() => {
        if (props.setClientEventListener) {
            props.setClientEventListener({
                identification: props.__halFormsTemplate.identifier || props.__halFormsTemplate.name,
                events: { 'RELOAD': true },
                onEvent: (event: any) => {
                    if (props.__dataSourceExtender) {
                        props.__dataSourceExtender.loadData(event.payload);
                    }
                }
            });
        }
    }, []);

    // End hooks

    // render progress
    if (props.__dataSourceExtender && props.__dataSourceExtender.isLoadingData) {
        return (
            <Box
                display='flex'
                width='100%'
                height='100%' 
            >
                <Box m='auto'>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    let container = null;
    let data = null;
    if (dataSource) {
        // container = (props.__dataSourceExtender) ? props.__dataSourceExtender.data : props.data;
        container = props.container;
        data = (props.__dataSourceExtender) ? props.__dataSourceExtender.data : null;
    } else {
        container = props.container;
        data = props.data;
    }

    const customContainerId = `container:${props.__halFormsTemplate.identifier}`;
    let CustomContainer = props.__componentsRegistry.getComponent(customContainerId);
    if (CustomContainer) {
        return (
            <CustomContainer
                __componentsRegistry={props.__componentsRegistry}
                __ducks={props.__ducks}
                __dataSourcesRef={props.__dataSourcesRef}
                __halFormsTemplate={props.__halFormsTemplate}
                onChange={props.onChange}
                onClose={props.onClose}
                onDataLoad={(d: any) => {
                    props.__dataSourceExtender.loadData(d);
                }}
                container={container}
                data={data}
            />
        );
    }

    return (
        <Grid
            container
            direction='column'
        >
            {(props.__halFormsTemplate.components || []).map((c, i, a) => {
                return (
                    <props.HalFormsContainerRow 
                        key={i}
                        isLast={i === a.length - 1}
                        title={c.title}
                    >
                        <HalFormsComponent
                            __componentsRegistry={props.__componentsRegistry}
                            __ducks={props.__ducks}
                            __dataSourcesRef={props.__dataSourcesRef}
                            __halFormsTemplate={props.__halFormsTemplate}
                            onChange={props.onChange}
                            onClose={props.onClose}
                            c={c}
                            container={container}
                            data={data}
                        />
                    </props.HalFormsContainerRow>
                );
            })}
        </Grid>
    );
}
const HalFormsContainerExt = withComponentsRegistry (
    withHalFormsAction ({ }) (
        withHalFormsDataSource (HalFormsContainer)
    )
);

export default HalFormsContainerExt;


